
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { useDemoMutations, useProfile, useProfileMutations, useFxRates } from "../hooks/queries";
import { useRolloverNotifications } from "../hooks/useRolloverNotifications";
import { useDarkMode } from "../hooks/useDarkMode";
import { FxStaleBanner } from "./FxStaleBanner";
import { DemoBanner } from "./DemoBanner";
import { DemoTour } from "./demo/DemoTour";
import { DemoTourProvider } from "./demo/DemoTourProvider";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { DesktopSidebar } from "./layout/DesktopSidebar";
import { MobileTopBar, MobileBottomNav } from "./layout/MobileNav";
import { SupportChatWidget } from "./support/SupportChatWidget";
import { InstallHintBanner } from "./pwa/InstallHintBanner";

export function Layout({ children }) {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showClear, setShowClear] = useState(false);
  const { seed, clear } = useDemoMutations();
  const { data: profile } = useProfile();
  const { update: updateProfile } = useProfileMutations();
  const { data: fxInfo } = useFxRates();
  // iter-213.8 — Apply the persisted dark theme on EVERY page mount.
  // Without this, navigating from /dashboard to e.g. /history would
  // unmount the Dashboard's `useDarkMode` instance and the `dark`
  // class would never get re-applied on the destination route.
  useDarkMode();
  // Heads-up toast when the overnight automation spawned recurring items.
  useRolloverNotifications();

  const currency = profile?.currency || "USD";
  const fxList = Object.keys(fxInfo?.rates || {}).sort();
  const isEs = lang === "es";

  const loadDemo = async () => {
    try {
      await seed.mutateAsync();
      toast.success("Demo data loaded");
    } catch (e) { console.error("load demo failed", e); }
  };

  const clearData = async () => {
    try {
      await clear.mutateAsync();
      toast.success("Data cleared");
      setShowClear(false);
    } catch (e) { console.error("clear data failed", e); }
  };

  const setCurrency = async (next) => {
    if (!next || next === currency) return;
    try {
      await updateProfile.mutateAsync({ currency: next });
      toast.success(`${next} ✓`);
    } catch (e) {
      console.error("set currency failed", e);
    }
  };

  const sharedProps = {
    t, lang, setLang, user, logout, navigate,
    currency, setCurrency, fxList, isEs,
  };

  return (
    <DemoTourProvider>
    <div className="min-h-screen flex bg-cream-100 dark:bg-slate-950">
      <DesktopSidebar
        {...sharedProps}
        loadDemo={loadDemo}
        onClearDemo={() => setShowClear(true)}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <DemoBanner />
        <InstallHintBanner />
        <MobileTopBar {...sharedProps} loadDemo={loadDemo} />
        <FxStaleBanner />
        <main className="flex-1 px-4 sm:px-6 lg:px-10 pt-0 sm:pt-6 pb-28 lg:pb-10 max-w-5xl w-full mx-auto lf-main-clip">
          {children}
        </main>
        <MobileBottomNav t={t} lang={lang} navigate={navigate} user={user} />
      </div>

      <AlertDialog open={showClear} onOpenChange={setShowClear}>
        <AlertDialogContent data-testid="clear-data-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("clear_demo")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm_clear_data")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-data-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="clear-data-confirm"
              onClick={clearData}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Floating support chat bubble — hidden until user is logged in. */}
      <SupportChatWidget />
      <DemoTour />
    </div>
    </DemoTourProvider>
  );
}
